import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { Pagination } from 'components/molecules/Pagination';
import {
  AmmunitionTableColumns,
  mapAmmunitionToDataSource,
} from 'pages/AncillariesPage/helpers';
import { ERouteLinks } from 'models/route';
import { Table } from 'components/molecules/Table';
import { useTranslations } from 'hooks/useTranslations';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useAlert } from 'models/alertContext';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ActionTypes } from 'state/actions/alert';
import { CardList } from 'components/molecules/CardList';
import { Loader } from 'components/atoms/Loader';
import { EPerPages, ISortOptions, ITableDataSource } from 'models/table';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import {
  customFieldsPrepareToSearch,
  objectToQueryString,
  prepareDataWithCustomFieldsToSearch,
  prepareQueryValues,
} from 'helpers/searchQuery';
import {
  IFormElementOptionProp,
  initialAncillariesSearchFormValues,
  ISearchAncillariesFormValues,
} from 'models/form';
import SearchFilterStore from 'state/SearchFilterStore';
import { useQuery } from 'hooks/useQuery';
import {
  EArtAncillaryExpandType,
  EArtAncillaryType,
  EArtAncillaryTypeName,
} from 'constants/ArtAccessoryType';
import {
  ECustomFieldObjectType,
  ECustomFieldType,
  ICustomFieldByObjectType,
} from 'models/customField';
import { useLang } from 'models/langContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EPermission } from 'models/permissions';
import { EChipsVariants } from 'components/atoms/Chips';
import { renderRowColor, renderRowColorHover } from 'components/molecules/Table/helpers';
import { formatOnlyDateToRequest } from 'utils/date';
import { Tooltip } from '@mui/material';
import { isExportNotAvailable } from 'constants/Export';
import { Box } from '@mui/system';
import { Alert, EAlertVariants, IAlert } from 'components/atoms/Alert';
import { EFirearmStateId } from 'models/firearm';
import { ISearchFormCustomFieldValue } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { useAncillaries } from '../hooks';
import { AmmunitionCard } from './AmmunitionCard';
import { AmmunitionSearchForm } from './AmmunitionSearchForm';
import { joinValuesToString } from 'helpers/joinValuesToString';
import { areObjectsEqual } from 'utils/objects';
import { replaceUrlIfOnPage } from 'utils/history';
import { exportAncillariesCSV } from 'requests/report';
import { EAssetType } from 'models/assetType';

interface AmmunitionPageProps {
  renderDataColor: (data: ITableDataSource) => {
    border: string;
    stateField: EChipsVariants;
    background: string;
  };
}

const AmmunitionPage = ({ renderDataColor }: AmmunitionPageProps) => {
  const { isDesktop } = useThemeBreakpoint();
  const { t } = useTranslations();
  const { pathname }: any = useLocation();
  const navigate = useNavigate();
  const [isAmmunitionLoading, setIsAmmunitionLoading] = useState<boolean>(true);
  const [searchParams] = useSearchParams();
  const urlQuery = new URLSearchParams(searchParams);
  const expandType = urlQuery.get('expand');
  const perPageFromQuery = Number.parseInt(searchParams.get('perPage') || '25', 10);
  const currentPageFromQuery = Number.parseInt(searchParams.get('currentPage') || '1', 10);
  const [currentPageAmmunition, setCurrentPageAmmunition] = useState<number>(
    expandType === EArtAncillaryExpandType.AMMUNITION ? currentPageFromQuery : 1,
  );
  const [perPageAmmunition, setPerPageAmmunition] = useState<EPerPages>(
    expandType === EArtAncillaryExpandType.AMMUNITION ? perPageFromQuery : EPerPages.perPage25,
  );
  const [ammunitionSort, setAmmunitionSort] = useState<ISortOptions | null>(null);
  const [ammunitionQuery, setAmmunitionQuery] = useState<
    Partial<ISearchAncillariesFormValues>
  >({});
  const [isAmmunitionQueryLoading, setIsAmmunitionQueryLoading] = useState<boolean>(false);
  const [isSortLoaded, setIsSortLoaded] = useState<boolean>(true);
  const query = useQuery();
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [ownerOptions, setOwnerOptions] = useState<IFormElementOptionProp[]>([]);
  const [keeperOptions, setKeeperOptions] = useState<IFormElementOptionProp[]>([]);
  const [initialValues, setInitialValues] = useState<ISearchAncillariesFormValues | null>(
    null,
  );
  const [areInitialValuesLoaded, setAreInitialValuesLoaded] = useState(false);
  const [areCustomFieldsLoaded, setAreCustomFieldsLoaded] = useState(false);
  const { selectedLanguage } = useLang();
  const { shortDateFormat } = useGlobalProperty();
  const { state: routerState }: any = useLocation();
  const isFromMapQuery: boolean =
    query.get('fromMap') === 'true' || routerState?.ammunitionUniqueIds?.length > 0;
  const storedAmmunition = SearchFilterStore.getAmmunition();
  const storedMapBounds = SearchFilterStore.getMapBounds();
  const [isExportLoading, setExportLoading] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { setAlert, clearAlert, ammunitionAlert } = useAlert();
  const { isAnyLicenceAuthorityValid } = usePrivilegesValidation();
  const [markedText, setMarkedText] = useState<string | undefined>();

  const scrollPosition = useRef(0);

  const uniqueIds =
    storedAmmunition.length > 0
      ? storedAmmunition.map((ammunitionItem) => ammunitionItem.uniqueId)
      : routerState?.ammunitionUniqueIds;

  const {
    paginatorAmmunition,
    ammunition,
    getAmmunitionData,
    getCustomField,
    customFieldsAmmunition,
  } = useAncillaries();

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_AMMUNITION_ALERT);
      setIsAmmunitionLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (isSortLoaded) {
      window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
      scrollPosition.current = 0;
    }
  }, [isSortLoaded]);

  useEffect(() => {
    (async () => {
      setAreCustomFieldsLoaded(false);
      await getCustomField(ECustomFieldObjectType.AN_AMMUNITION);
      setAreCustomFieldsLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const previousAmmunitionSortRef = useRef(ammunitionSort);

  useEffect(() => {
    (async function getDataInit() {
      setIsPaginationLoading(true);
      if (!ammunition.length) {
        setIsAmmunitionLoading(true);
      }
      if (isFromMapQuery) {
        if (storedMapBounds && storedMapBounds.assetType === EAssetType.AMMUNITION) {
          ammunitionQuery.minLatitude = storedMapBounds.minLatitude;
          ammunitionQuery.maxLatitude = storedMapBounds.maxLatitude;
          ammunitionQuery.minLongitude = storedMapBounds.minLongitude;
          ammunitionQuery.maxLongitude = storedMapBounds.maxLongitude;

          delete ammunitionQuery.uniqueIds;
        } else if (uniqueIds && uniqueIds.length) {
          ammunitionQuery.uniqueIds = uniqueIds;

          delete ammunitionQuery.minLatitude;
          delete ammunitionQuery.maxLatitude;
          delete ammunitionQuery.minLongitude;
          delete ammunitionQuery.maxLongitude;
        } else {
          delete ammunitionQuery.uniqueIds;

          delete ammunitionQuery.minLatitude;
          delete ammunitionQuery.maxLatitude;
          delete ammunitionQuery.minLongitude;
          delete ammunitionQuery.maxLongitude;
        }
      }

      if (ammunitionSort !== previousAmmunitionSortRef.current) {
        setIsSortLoaded(false);
      }

      if (Object.keys(ammunitionQuery).length !== 0) {
        customFieldsPrepareToSearch(ammunitionQuery.customFields);
        const ammunitionQueryData = prepareDataWithCustomFieldsToSearch(
          ammunitionQuery,
          customFieldsAmmunition.filter(
            (item: ICustomFieldByObjectType) => item.searchCriteria,
          ),
        );
        await getAmmunitionData(
          currentPageAmmunition,
          perPageAmmunition,
          {
            ...ammunitionQueryData,
          },
          ammunitionSort,
        );
        setIsAmmunitionLoading(false);
      }

      setIsAmmunitionQueryLoading(false);
      setIsPaginationLoading(false);
      setIsSortLoaded(true);
      previousAmmunitionSortRef.current = ammunitionSort;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ammunitionQuery, ammunitionSort, selectedLanguage]);

  useEffect(() => {
    if (initialValues !== undefined && areCustomFieldsLoaded) {
      setAreInitialValuesLoaded(true);
    }
  }, [initialValues, areCustomFieldsLoaded]);

  useEffect(() => {
    if (areCustomFieldsLoaded) {
      setAreInitialValuesLoaded(false);
      if (expandType === EArtAncillaryExpandType.AMMUNITION) {
        setShowAdvanced(true);
        const queryValues = prepareQueryValues(
          {
            ...initialAncillariesSearchFormValues,
            perPage: perPageAmmunition,
            currentPage: currentPageAmmunition,
          },
          urlQuery,
          [
            'manufactureDateFrom',
            'manufactureDateTo',
            'expiryDateFrom',
            'expiryDateTo',
            ...customFieldsAmmunition
              .filter((cf) => cf.customFieldType === ECustomFieldType.DATE)
              .map((cf) => [`from_${cf.name}`, `to_${cf.name}`]),
          ].flat(),
        );
        setInitialValues({ ...initialAncillariesSearchFormValues, ...queryValues });
      } else {
        setInitialValues(initialAncillariesSearchFormValues);
        setAreInitialValuesLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, areCustomFieldsLoaded]);

  const getNewQueryPath = (values: Partial<ISearchAncillariesFormValues>): string => {
    const valuesWithoutPreset = {
      ...values,
    };
    valuesWithoutPreset.preset = undefined;
    valuesWithoutPreset.formSubmitted = undefined;
    valuesWithoutPreset.perPage = perPageAmmunition;
    valuesWithoutPreset.currentPage = currentPageAmmunition;

    const valuesToString = {
      ...valuesWithoutPreset,
      accessoryCapacities: joinValuesToString(values.accessoryCapacities),
      calibresUniqueIds: joinValuesToString(values.calibresUniqueIds),
      cartridgeCompositionsUniqueIds: joinValuesToString(
        values.cartridgeCompositionsUniqueIds,
      ),
      coloursOfPrimerSealantUniqueIds: joinValuesToString(
        values.coloursOfPrimerSealantUniqueIds,
      ),
      componentCapacitiesUniqueIds: joinValuesToString(values.componentCapacitiesUniqueIds),
      componentmanufacturersUniqueIds: joinValuesToString(
        values.componentmanufacturersUniqueIds,
      ),
      compositionsUniqueIds: joinValuesToString(values.compositionsUniqueIds),
      factoriesUniqueIds: joinValuesToString(values.factoriesUniqueIds),
      frtMakesUniqueIds: joinValuesToString(values.frtMakesUniqueIds),
      frtManufacturersUniqueIds: joinValuesToString(values.frtManufacturersUniqueIds),
      frtModelsUniqueIds: joinValuesToString(values.frtModelsUniqueIds),
      frtTypesUniqueIds: joinValuesToString(values.frtTypesUniqueIds),
      functionalTypesUniqueIds: joinValuesToString(values.functionalTypesUniqueIds),
      hostTypesUniqueIds: joinValuesToString(values.hostTypesUniqueIds),
      lightColoursUniqueIds: joinValuesToString(values.lightColoursUniqueIds),
      magnificationsUniqueIds: joinValuesToString(values.magnificationsUniqueIds),
      manufacturersUniqueIds: joinValuesToString(values.manufacturersUniqueIds),
      munitionTypesUniqueIds: joinValuesToString(values.munitionTypesUniqueIds),
      munitionVelocitiesUniqueIds: joinValuesToString(values.munitionVelocitiesUniqueIds),
      projectileShapesUniqueIds: joinValuesToString(values.projectileShapesUniqueIds),
      proofHousesUniqueIds: joinValuesToString(values.proofHousesUniqueIds),
      reticlesUniqueIds: joinValuesToString(values.reticlesUniqueIds),
      shotSizesUniqueIds: joinValuesToString(values.shotSizesUniqueIds),
      statesUniqueIds: joinValuesToString(values.statesUniqueIds),
      utilitiesUniqueIds: joinValuesToString(values.utilitiesUniqueIds),
    };

    const objQuery = objectToQueryString(valuesToString, false, shortDateFormat);

    if (
      objQuery.length &&
      (!isInitialLoad || expandType === EArtAncillaryExpandType.AMMUNITION)
    ) {
      setIsInitialLoad(false);
      return `${pathname}?expand=ammunition&${objQuery}`;
    }
    if (values.formSubmitted === true) {
      return `${pathname}?expand=ammunition`;
    }
    setIsInitialLoad(false);
    return `${pathname}`;
  };

  const areCustomFieldValuesSame = (
    cfValues: ISearchFormCustomFieldValue[] | undefined,
    queryValues: ISearchFormCustomFieldValue[] | undefined,
  ): boolean => {
    if (!cfValues || !queryValues) {
      return true;
    }
    let areSame = true;
    cfValues.forEach((cfValue) => {
      const queryCf = queryValues.find(
        (it) => it.customFieldUniqueId === cfValue.customFieldUniqueId,
      );
      if (
        cfValue.bool !== queryCf?.bool ||
        cfValue.text !== queryCf?.text ||
        cfValue.dateFrom !== queryCf?.dateFrom ||
        cfValue.dateTo !== queryCf?.dateTo
      ) {
        areSame = false;
      }
    });

    return areSame;
  };

  const areDatesEqual = (
    date1: Date | null | undefined | string,
    date2: Date | null | undefined | string,
  ): boolean => {
    if (!date1 && !date2) return true;
    if (!date1 || !date2) return false;

    return new Date(date1).getTime() === new Date(date2).getTime();
  };

  const handleSetAmmunitionQuery = (values: Partial<ISearchAncillariesFormValues>) => {
    if (values.invokedByPageChange !== true) {
      setIsAmmunitionQueryLoading(true);
    }
    values.invokedByPageChange = undefined;

    const valuesWithoutPreset = { ...values };
    valuesWithoutPreset.preset = undefined;
    valuesWithoutPreset.formSubmitted = undefined;

    if (isInitialLoad) {
      setIsInitialLoad(false);
    } else {
      replaceUrlIfOnPage(getNewQueryPath(values));

      if (
        values.ammunitionTypeUniqueId !== ammunitionQuery.ammunitionTypeUniqueId ||
        values.fireCycleId !== ammunitionQuery.fireCycleId ||
        values.lotNumber !== ammunitionQuery.lotNumber ||
        values.marking !== ammunitionQuery.marking ||
        values.batchNumber !== ammunitionQuery.batchNumber ||
        values.exhibitNumber !== ammunitionQuery.exhibitNumber ||
        !areObjectsEqual(
          values.manufacturersUniqueIds,
          ammunitionQuery.manufacturersUniqueIds,
        ) ||
        values.productName !== ammunitionQuery.productName ||
        !areObjectsEqual(values.factoriesUniqueIds, ammunitionQuery.factoriesUniqueIds) ||
        !areDatesEqual(values.manufactureDateFrom, ammunitionQuery.manufactureDateFrom) ||
        !areDatesEqual(values.manufactureDateTo, ammunitionQuery.manufactureDateTo) ||
        !areDatesEqual(values.expiryDateFrom, ammunitionQuery.expiryDateFrom) ||
        !areDatesEqual(values.expiryDateTo, ammunitionQuery.expiryDateTo) ||
        !areObjectsEqual(values.calibresUniqueIds, ammunitionQuery.calibresUniqueIds) ||
        !areObjectsEqual(
          values.functionalTypesUniqueIds,
          ammunitionQuery.functionalTypesUniqueIds,
        ) ||
        !areObjectsEqual(
          values.cartridgeCompositionsUniqueIds,
          ammunitionQuery.cartridgeCompositionsUniqueIds,
        ) ||
        !areObjectsEqual(
          values.coloursOfPrimerSealantUniqueIds,
          ammunitionQuery.coloursOfPrimerSealantUniqueIds,
        ) ||
        !areObjectsEqual(
          values.projectileShapesUniqueIds,
          ammunitionQuery.projectileShapesUniqueIds,
        ) ||
        !areObjectsEqual(
          values.lightColoursUniqueIds,
          ammunitionQuery.lightColoursUniqueIds,
        ) ||
        !areObjectsEqual(values.shotSizesUniqueIds, ammunitionQuery.shotSizesUniqueIds) ||
        !areObjectsEqual(
          values.munitionTypesUniqueIds,
          ammunitionQuery.munitionTypesUniqueIds,
        ) ||
        !areObjectsEqual(
          values.munitionVelocitiesUniqueIds,
          ammunitionQuery.munitionVelocitiesUniqueIds,
        ) ||
        values.headstampYear !== ammunitionQuery.headstampYear ||
        values.alphanumericalMarkins !== ammunitionQuery.alphanumericalMarkins ||
        values.legalityUniqueId !== ammunitionQuery.legalityUniqueId ||
        !areObjectsEqual(values.statesUniqueIds, ammunitionQuery.statesUniqueIds) ||
        !areCustomFieldValuesSame(values.customFields, ammunitionQuery.customFields) ||
        values.keeperUniqueId !== ammunitionQuery.keeperUniqueId ||
        values.ownerUniqueId !== ammunitionQuery.ownerUniqueId
      ) {
        setCurrentPageAmmunition(1);
      }
    }

    if (valuesWithoutPreset.expiryDateFrom) {
      valuesWithoutPreset.expiryDateFrom = formatOnlyDateToRequest(
        valuesWithoutPreset.expiryDateFrom,
      );
    }
    if (valuesWithoutPreset.expiryDateTo) {
      valuesWithoutPreset.expiryDateTo = formatOnlyDateToRequest(
        valuesWithoutPreset.expiryDateTo,
      );
    }
    if (valuesWithoutPreset.manufactureDateFrom) {
      valuesWithoutPreset.manufactureDateFrom = formatOnlyDateToRequest(
        valuesWithoutPreset.manufactureDateFrom,
      );
    }
    if (valuesWithoutPreset.manufactureDateTo) {
      valuesWithoutPreset.manufactureDateTo = formatOnlyDateToRequest(
        valuesWithoutPreset.manufactureDateTo,
      );
    }

    setMarkedText(values.marking);

    setAmmunitionQuery(valuesWithoutPreset);
  };

  const exportCsv = async () => {
    setExportLoading(true);
    ammunitionQuery.ancillaryDictionaryUniqueId = EArtAncillaryType.AMMUNITION;
    await exportAncillariesCSV(ammunitionQuery, ammunitionSort);
    setExportLoading(false);
  };

  const handleRedirectToEditPageButton = (item: ITableDataSource) => {
    if (
      customFieldsAmmunition?.length &&
      !EFirearmStateId.DISABLED.includes(item.stateUniqueId)
    ) {
      navigate(
        ERouteLinks.EditAncillary.replace(':id', item.id as string).replace(
          ':type',
          'ammunition',
        ),
      );
    } else if (EFirearmStateId.DISABLED.includes(item.stateUniqueId)) {
      setAlert(ActionTypes.SET_AMMUNITION_ALERT, {
        text: t('ancillary.disabled.edit.info'),
        variant: EAlertVariants.error,
      } as IAlert);
    } else {
      setAlert(ActionTypes.SET_AMMUNITION_ALERT, {
        text: t('general.noAvaibleDataToEdit.label'),
        variant: EAlertVariants.warning,
      } as IAlert);
    }
  };

  return (
    <div>
      {initialValues && areInitialValuesLoaded && areCustomFieldsLoaded && (
        <AmmunitionSearchForm
          ownerOptions={ownerOptions}
          keeperOptions={keeperOptions}
          setOwnerOptions={setOwnerOptions}
          setKeeperOptions={setKeeperOptions}
          showAdvanced={showAdvanced}
          setShowAdvanced={setShowAdvanced}
          ammunitionUniqueIds={uniqueIds}
          isFromMap={isFromMapQuery}
          returnPath={`${pathname}?expand=ammunition`}
          getNewQueryPath={getNewQueryPath}
          onSubmit={handleSetAmmunitionQuery}
          isSearchButtonDisabled={isAmmunitionLoading || isAmmunitionQueryLoading}
          initialFormValues={initialValues ?? undefined}
          customFields={customFieldsAmmunition.filter(
            (item: ICustomFieldByObjectType) => item.searchCriteria,
          )}
          currentPage={currentPageAmmunition}
          perPage={perPageAmmunition}
        />
      )}
      <Grid sx={{ display: 'flex', justifyContent: 'flex-end', margin: '10px 0', gap: 1 }}>
        {!isAmmunitionLoading && (
          <>
            {isExportNotAvailable(paginatorAmmunition) ? (
              <Tooltip title={t('dataExportRecordsLimitAmount.label')} arrow>
                <span>
                  <Button
                    variant={EButtonVariants.outlined}
                    size={EButtonSizes.medium}
                    permission={[EPermission.EXPORT_ANCILLARIES]}
                    label={t('ancillaries.accordion.export.button')}
                    type="submit"
                    onClick={exportCsv}
                    isLoading={isExportLoading}
                    disabled
                  />
                </span>
              </Tooltip>
            ) : (
              <Button
                variant={EButtonVariants.outlined}
                size={EButtonSizes.medium}
                permission={[EPermission.EXPORT_ANCILLARIES]}
                label={t('ancillaries.accordion.export.button')}
                type="submit"
                onClick={exportCsv}
                isLoading={isExportLoading}
              />
            )}
            <Button
              variant={EButtonVariants.contained}
              size={EButtonSizes.medium}
              label={t('ancillaries.accordion.showOnMap.button')}
              onClick={() => {
                navigate(ERouteLinks.RegisteredAncillariesMap, {
                  state: {
                    ancillaryType: 'ammunition',
                    ancillaryQuery: ammunitionQuery,
                    showAdvanced,
                    options: { ownerOptions, keeperOptions },
                    navigateBack: -1,
                  },
                });
              }}
            />
          </>
        )}
      </Grid>
      {isAmmunitionLoading || isAmmunitionQueryLoading ? (
        <Loader isLoading={true} />
      ) : (
        <>
          {isDesktop ? (
            <Table
              columns={AmmunitionTableColumns(
                {
                  handleEdit: handleRedirectToEditPageButton,
                  customFields: customFieldsAmmunition,
                  t,
                  isAnyLicenceAuthorityValid,
                  markedText: markedText,
                  showOnMap: (item) => {
                    navigate(ERouteLinks.RegisteredAncillariesMap, {
                      state: {
                        ancillaryUniqueId: item?.id,
                        ancillaryType: EArtAncillaryTypeName.AMMUNITION.toLowerCase(),
                        legalityUniqueId: item?.legalityUniqueId,
                        navigateBack: `${ERouteLinks.Ancillaries}?expand=ammunition`,
                        showAdvanced: true,
                        showLegalityBar: false,
                        breadcrumbs: [
                          {
                            label: t('module.ancillaries.name'),
                            route: ERouteLinks.Ancillaries,
                          },
                          {
                            label: t('module.ammunition.name'),
                            route: `${ERouteLinks.Ancillaries}?expand=ammunition`,
                          },
                          t('ancillaries.ancillariesLocation.showOnMap.label'),
                        ],
                      },
                    });
                  },
                },
                (data: ITableDataSource) => renderDataColor(data).stateField,
                selectedLanguage?.uniqueId,
                shortDateFormat,
              )}
              specificRowColor={renderRowColor}
              specificRowColorHover={renderRowColorHover}
              dataSource={mapAmmunitionToDataSource(ammunition)}
              onSort={(it) => {
                setAmmunitionSort(it);
                scrollPosition.current = window.scrollY;
              }}
              isSortLoaded={isSortLoaded}
              customPaddingIfNoData="30px"
            />
          ) : (
            <CardList
              items={mapAmmunitionToDataSource(ammunition)}
              render={(data, index) => (
                <AmmunitionCard
                  data={data}
                  key={index}
                  renderColor={renderDataColor}
                  handleView={(id: string) =>
                    navigate(
                      ERouteLinks.ViewAncillary.replace(':id', id).replace(
                        ':type',
                        'ammunition',
                      ),
                    )
                  }
                  hasCustomFields={!!customFieldsAmmunition.length}
                  handleEdit={handleRedirectToEditPageButton}
                  isAnyLicenceAuthorityValid={isAnyLicenceAuthorityValid}
                />
              )}
            />
          )}

          {paginatorAmmunition && (
            <Pagination
              count={paginatorAmmunition?.totalElements}
              perPage={perPageAmmunition}
              onChangePage={(page) => {
                setCurrentPageAmmunition(page);
              }}
              onChangePerPage={(value) => {
                setPerPageAmmunition(value);
              }}
              current={currentPageAmmunition}
              isLoading={isPaginationLoading}
              isVisible={isSortLoaded}
            />
          )}
        </>
      )}
      {ammunitionAlert && (
        <Box sx={{ marginBottom: '10px', mt: 2 }}>
          <Alert text={ammunitionAlert.text} variant={ammunitionAlert.variant} />
        </Box>
      )}
    </div>
  );
};

export default AmmunitionPage;
